import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { SxProps, Theme } from "@mui/material/styles";


import {
    CmCollectionType,
    CmLinkableType,
    PageGridPlacementType,
} from "../../types";
import { usePageContext } from "../../../context/PageContext";
import useWindowSize from "../../../utility/useWindowSize";

import {
    toolbarButtonHoveredSx,
    toolbarButtonSx,
} from "./core/navigationStyles";
import { handleMenuItemKeyboardNav } from "./core/keyboardNavigationHandlers";
import {
    MenuItemElement,
    NavigationMenuItem,
} from "./core/navigationTypes";
import { blurActiveElement } from "./core/navigationFocusUtils";
import {
    getNavigationRoot,
    hasChildren,
    isClickInsideRef,
    isSameMenuItem,
} from "./core/navigationItemUtils";
import {useNavigationTracking} from "tracking/hooks/useNavigationTracking";

interface UseNavigationMenuParams {
    navigation: any;
    page: any;
    header?: PageGridPlacementType;
}

interface TopLevelLinkProps {
    ref: (element: HTMLAnchorElement | null) => void;
    onClick: React.MouseEventHandler<HTMLAnchorElement>;
    onMouseEnter: React.MouseEventHandler<HTMLAnchorElement>;
    onFocus: React.FocusEventHandler<HTMLAnchorElement>;
    onBlur: React.FocusEventHandler<HTMLAnchorElement>;
    onKeyDown: React.KeyboardEventHandler<HTMLAnchorElement>;
}

interface NavigationMenuViewModel {
    layout: {
        hideTopNavi: boolean;
        greaterThanWindowLarge: boolean;
    };
    desktopMenu: {
        isVisible: boolean;
        items: NavigationMenuItem[];
        appBarRef: React.RefObject<HTMLDivElement | null>;
        getItemSx(menuItem: NavigationMenuItem, index: number): SxProps<Theme>;
        getLinkProps(menuItem: NavigationMenuItem, index: number): TopLevelLinkProps;
    };
    mobileMenu: {
        isVisible: boolean;
        items: CmLinkableType[];
        open: boolean;
        topNaviItems: CmLinkableType[];
        toggle(): void;
        close(): void;
    };
    appBarMenu: {
        isVisible: boolean;
        props: {
            closeMenu(): void;
            page: any;
            isMenuOpen: boolean;
            selection: NavigationMenuItem | null;
            popoverRef: React.RefObject<HTMLDivElement | null>;
            firstPopoverItemRef: React.MutableRefObject<MenuItemElement | null>;
            getMainNavItemRef: (offset: number) => HTMLAnchorElement | null;
            isFlyoutSmall?: boolean;
        };
    };
}

const EMPTY_NAVIGATION_ITEMS: NavigationMenuItem[] = [];

const normalizeUrl = (url?: string | null): string =>
    (url ?? "").split(/[?#]/)[0].replace(/\/+$/, "");

export const useNavigationMenu = ({
                                      navigation,
                                      page,
                                      header,
                                  }: UseNavigationMenuParams): NavigationMenuViewModel => {
    const {trackMainNavigation} = useNavigationTracking();
    const appBarRef = useRef<HTMLDivElement | null>(null);
    const popoverRef = useRef<HTMLDivElement | null>(null);
    const menuItemRefs = useRef<Array<HTMLAnchorElement | null>>([]);
    const firstPopoverItemRef = useRef<MenuItemElement | null>(null);
    const lastFocusedMenuItemIndex = useRef<number | null>(null);

    const { greaterThanWindowLarge, lowerThanWindowLarge } = useWindowSize();
    const { hideTopNavi } = usePageContext();

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [activeMenuItem, setActiveMenuItem] =
        useState<NavigationMenuItem | null>(null);
    const [focusedMenuItemIndex, setFocusedMenuItemIndex] =
        useState<number | null>(null);

    const navigationRoot = useMemo(() => {
        return getNavigationRoot(navigation);
    }, [navigation]);

    const navigationItems = useMemo(() => {
        return navigationRoot?.children ?? EMPTY_NAVIGATION_ITEMS;
    }, [navigationRoot]);

    const mobileNavigationItems = useMemo(() => {
        return navigationItems.filter(
            (item): item is NavigationMenuItem & { id: string } =>
                typeof item.id === "string"
        ) as unknown as CmLinkableType[];
    }, [navigationItems]);

    const topNaviItems = useMemo(() => {
        const topNavi = header?.items?.find(
            (cmCol: any) => cmCol.viewtype === "asTopNavi"
        ) as CmCollectionType | undefined;

        return (topNavi?.items ?? []).filter(Boolean) as CmLinkableType[];
    }, [header]);

    const closeMenu = useCallback(() => {
        setIsMenuOpen(false);
        setActiveMenuItem(null);
        setFocusedMenuItemIndex(null);
    }, []);

    const closeMobileMenu = useCallback(() => {
        setIsMenuOpen(false);
    }, []);

    const toggleMobileMenu = useCallback(() => {
        setIsMenuOpen((open) => !open);
    }, []);

    const getMainNavItemRef = useCallback(
        (offset: number): HTMLAnchorElement | null => {
            const currentIndex = lastFocusedMenuItemIndex.current;

            if (currentIndex === null) {
                return null;
            }

            return menuItemRefs.current[currentIndex + offset] ?? null;
        },
        []
    );

    useEffect(() => {
        if (!greaterThanWindowLarge || !isMenuOpen) {
            return;
        }

        const handleClickAway = (event: MouseEvent) => {
            const target = event.target;

            if (!(target instanceof Node)) {
                return;
            }

            const clickedInsideNavigation =
                isClickInsideRef(appBarRef, target) ||
                isClickInsideRef(popoverRef, target);

            if (!clickedInsideNavigation) {
                closeMenu();
            }
        };

        document.addEventListener("mousedown", handleClickAway);

        return () => {
            document.removeEventListener("mousedown", handleClickAway);
        };
    }, [closeMenu, greaterThanWindowLarge, isMenuOpen]);

    const openTopLevelMenu = useCallback(
        (menuItem: NavigationMenuItem, index: number) => {
            lastFocusedMenuItemIndex.current = index;
            setActiveMenuItem(menuItem);
            setIsMenuOpen(true);
            setFocusedMenuItemIndex(index);
        },
        []
    );

    const handleTopLevelMenuClick = useCallback(
        (
            event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
            menuItem: NavigationMenuItem,
            index: number
        ) => {
            lastFocusedMenuItemIndex.current = index;

            if (!hasChildren(menuItem)) {
                trackMainNavigation(0, menuItem.teaserTitle);
                closeMenu();
                return;
            }

            event.preventDefault();
            blurActiveElement();

            if (isSameMenuItem(activeMenuItem, menuItem)) {
                closeMenu();
                return;
            }

            openTopLevelMenu(menuItem, index);
        },
        [activeMenuItem, closeMenu, openTopLevelMenu, trackMainNavigation]
    );

    const handleTopLevelMenuMouseEnter = useCallback(() => {
        blurActiveElement();
    }, []);

    const handleTopLevelMenuFocus = useCallback((index: number) => {
        lastFocusedMenuItemIndex.current = index;
        setFocusedMenuItemIndex(index);
    }, []);

    const handleTopLevelMenuBlur = useCallback(() => {
        setFocusedMenuItemIndex(null);
    }, []);

    const handleTopLevelMenuKeyDown = useCallback(
        (
            event: React.KeyboardEvent,
            menuItem: NavigationMenuItem,
            index: number
        ) => {
            handleMenuItemKeyboardNav({
                                          event,
                                          index,
                                          menuItem,
                                          activeMenuItem,
                                          setIsMenuOpen,
                                          setActiveMenuItem,
                                          setFocusedMenuItemIndex,
                                          lastFocusedMenuItemIndex,
                                          firstPopoverItemRef,
                                      });
        },
        [activeMenuItem]
    );

    const getTopLevelItemSx = useCallback(
        (menuItem: NavigationMenuItem, index: number): SxProps<Theme> => {
            const isFocused = focusedMenuItemIndex === index;
            const isOpenAndActive =
                isMenuOpen && isSameMenuItem(activeMenuItem, menuItem);

            const currentUrl = normalizeUrl(page?.selfUrl);
            const menuUrl = normalizeUrl(menuItem.selfUrl);

            const isCurrentPage =
                Boolean(menuUrl) &&
                (currentUrl === menuUrl || currentUrl.startsWith(`${menuUrl}/`));

            return isFocused || isOpenAndActive || isCurrentPage
                ? toolbarButtonHoveredSx as SxProps<Theme>
                : toolbarButtonSx as SxProps<Theme>;
        },
        [activeMenuItem, focusedMenuItemIndex, isMenuOpen, page?.selfUrl]
    );

    const getDesktopMenuItemLinkProps = useCallback(
        (menuItem: NavigationMenuItem, index: number): TopLevelLinkProps => ({
            ref: (element: HTMLAnchorElement | null) => {
                menuItemRefs.current[index] = element;
            },
            onClick: (event) =>
                handleTopLevelMenuClick(event, menuItem, index),
            onMouseEnter: handleTopLevelMenuMouseEnter,
            onFocus: () => handleTopLevelMenuFocus(index),
            onBlur: handleTopLevelMenuBlur,
            onKeyDown: (event) =>
                handleTopLevelMenuKeyDown(event, menuItem, index),
        }),
        [
            handleTopLevelMenuBlur,
            handleTopLevelMenuClick,
            handleTopLevelMenuFocus,
            handleTopLevelMenuKeyDown,
            handleTopLevelMenuMouseEnter,
        ]
    );

    const appBarMenuProps = useMemo(
        () => ({
            closeMenu,
            page,
            isMenuOpen,
            selection: activeMenuItem,
            popoverRef,
            firstPopoverItemRef,
            getMainNavItemRef,
            isFlyoutSmall: page?.settings?.isFlyoutSmall,
        }),
        [
            activeMenuItem,
            closeMenu,
            getMainNavItemRef,
            isMenuOpen,
            page,
        ]
    );

    return useMemo(
        () => ({
            layout: {
                hideTopNavi,
                greaterThanWindowLarge,
            },
            desktopMenu: {
                isVisible: greaterThanWindowLarge && Boolean(navigationRoot),
                items: navigationItems,
                appBarRef,
                getItemSx: getTopLevelItemSx,
                getLinkProps: getDesktopMenuItemLinkProps,
            },
            mobileMenu: {
                isVisible: lowerThanWindowLarge,
                items: mobileNavigationItems,
                open: isMenuOpen,
                topNaviItems,
                toggle: toggleMobileMenu,
                close: closeMobileMenu,
            },
            appBarMenu: {
                isVisible: isMenuOpen && greaterThanWindowLarge,
                props: appBarMenuProps,
            },
        }),
        [
            appBarMenuProps,
            closeMobileMenu,
            getDesktopMenuItemLinkProps,
            getTopLevelItemSx,
            greaterThanWindowLarge,
            hideTopNavi,
            isMenuOpen,
            lowerThanWindowLarge,
            mobileNavigationItems,
            navigationItems,
            navigationRoot,
            toggleMobileMenu,
            topNaviItems,
        ]
    );
};